/**
 * Disposition Framework screen (design option 4a — turn4 developer handoff).
 *
 * Replaces the old four-box Framework Mapping screen with the six-outcome / two-axis engine's
 * view: outcome tiles → drill-down (dot plot + Top-EUC list) → the six-question projection +
 * a per-EUC Worked Example. The screen is a VIEW of DispositionRecords + a RuleSet (spec §8);
 * every rendered value re-derives from a record — no per-EUC math here beyond formatting.
 *
 * Data is fixtures for now (the engine's records aren't served yet). Purple palette only, per
 * the design brief — no red/orange/green tiles.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { Band, BoxId, ClusterRecord, DispositionRecord, RuleSet } from './types';
import { BAND_RANK } from './types';
import { RULESET, QUESTIONS, PRIMITIVES } from './fixtures';
import Clusters from './Clusters';
import { useFrameworkData } from './useFrameworkData';
import { useFrameworkConfig } from './useFrameworkConfig';
import { useRuleConfig } from './useRuleConfig';
import { useProject } from '../../../contexts/ProjectContext';
import { color, font } from '../tokens';

// Re-anchored to Jarvis tokens (task 1218070214136694 · unify visual language). Was a bespoke
// purple palette (accent #6a4cf5 / deep #4d2fe0 / Manrope) that made this — the most-visited LD
// screen — read as a separate product from the MA header. Now: amber accent, super-dark-purple
// ink, Inter via font.ui. The `C` shape is unchanged so every inline style below inherits it.
const C = {
  accent: color.accent, deep: color.navy, ink: '#1e1b3a',
  body: '#4a4966', subtle: color.textMuted, label: '#7c7a95', border: color.cardBorder, soft: '#f9fafb', surface: color.surface,
};
// Box tint strip — a super-dark-purple → lavender ramp (WHERE axis), distinct from the amber
// action accent so a box tile never looks like a button.
const BOX_STRIP: Record<BoxId, string> = { box1: '#221551', box2: '#5b3fc4', box3: '#8b5cf6', box4: '#c4b5fd' };
const RISK_TEXT: Record<string, string> = { HIGH: '#221551', MEDIUM: '#8b5cf6', LOW: '#a78bfa' };
const TIMING_LABEL = { cat1: 'Cat 1 — Immediate', cat2: 'Cat 2 — Near-term', cat3: 'Cat 3 — Deferred' } as const;
const money = (n?: number | null) => (n == null ? '—' : n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : `$${Math.round(n / 1e3)}k`);
// Materiality is a band; a Finance $ override, when present, shows alongside it.
const BAND_COLOR: Record<Band, { bg: string; fg: string }> = {
  Critical: { bg: '#fde2e1', fg: '#9b1c1c' }, High: { bg: '#fef0d9', fg: '#9a5b00' },
  Moderate: { bg: '#e6eafc', fg: '#3a3a8a' }, Low: { bg: '#eef0f5', fg: '#5b5b73' },
};
const bandLabel = (r: DispositionRecord) => (r.materiality_band ? r.materiality_band + (r.materiality_usd ? ` · ${money(r.materiality_usd)}` : '') : '—');
const bandRankOf = (r: DispositionRecord) => (r.materiality_band ? BAND_RANK[r.materiality_band] : 0);
// The Top EUCs order (Ryan 2026-07-21: band, then confidence). Shared, because the default
// selection has to agree with it — see the `selected` fallback below.
const byBandThenConfidence = (a: DispositionRecord, b: DispositionRecord) =>
  bandRankOf(b) - bandRankOf(a) || (b.confidence || 0) - (a.confidence || 0);
const topRanked = (rows: DispositionRecord[]) =>
  rows.reduce<DispositionRecord | undefined>((best, r) => (!best || byBandThenConfidence(r, best) < 0 ? r : best), undefined);

// Edits 5 & 7 — the target systems each outcome routes to + the captured per-EUC answers are now
// persisted per-tenant in the DB (framework_snapshots) through useFrameworkConfig, replacing the
// earlier browser-only localStorage. See useFrameworkConfig.ts.

interface Props {
  ruleset?: RuleSet; records?: DispositionRecord[]; clusters?: ClusterRecord[];
  onAdjustFramework?: () => void;
  // MECE IA (task 1218070214136694, subtask 4): Framework Mapping is WHERE × WHEN only. When an
  // owner (MA workspace / standalone dashboard) provides this, the Consolidate / Decommission chips
  // NAVIGATE to the dedicated Consolidation tab instead of mounting Clusters inline here — one job
  // per screen. Falls back to the old inline drill only if not provided (kept for storybook/tests).
  onNavigateConsolidation?: () => void;
}

export default function DispositionFramework({ ruleset: rulesetProp, records: recordsProp, clusters: clustersProp, onAdjustFramework, onNavigateConsolidation }: Props) {
  // Live data from /api/framework when the route is mounted; the demo fixtures otherwise. Explicit
  // props (tests / storybook) override the hook.
  const live = useFrameworkData();
  // QA finding 2026-08-24 (subtask "Revisions to Framework Mapping"): this screen is only ever
  // mounted WITHOUT a `ruleset` prop, so it rendered the fixture's ruleset — the header read
  // "Ruleset v7" while every record on the same page read "pinned to ruleset v1", and the box
  // definitions came from a frontend file rather than the tenant's published ruleset (so tuning a
  // box in Rule Configuration and publishing it would never show up here). Read the effective
  // ruleset from /rule-config instead. Precedence: an explicit prop (tests / storybook) wins, then
  // the live ruleset, then the fixture as a pre-load fallback so nothing flashes empty.
  const ruleCfg = useRuleConfig();
  const ruleset: RuleSet = rulesetProp
    || (ruleCfg.effective
      ? { version: ruleCfg.effective.version, published_at: ruleCfg.effective.published_at ?? undefined, boxes: ruleCfg.effective.boxes as RuleSet['boxes'] }
      : RULESET);
  const records = recordsProp || live.records;
  const clusters = clustersProp || live.clusters;
  const [selectedBox, setSelectedBox] = useState<BoxId>('box2');
  const [selectedEucId, setSelectedEucId] = useState<string>(records[0]?.euc_id);
  const [showClusters, setShowClusters] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [ingestMsg, setIngestMsg] = useState('');
  // Edits 5 & 7 — target systems + captured answers, persisted per-tenant in the DB
  // (framework_snapshots) via this hook. Tiles fall back to the ruleset catalog[0] until loaded.
  const { targets, customTargets, eucTargets, answers, setTarget, addCustomTarget, setEucTarget, setAnswer } = useFrameworkConfig();
  // Item 7 (Asana 1216373781992284): a per-EUC platform pick is saved, then records are
  // reloaded so the worked example reads back the server-resolved selected_target_platform —
  // the same round trip Edit-7's box-level setTarget already implied, just one level down.
  const onSetEucTarget = async (eucId: string, value: string) => {
    setEucTarget(eucId, value);
    await live.reload();
  };
  const { currentProject } = useProject();

  // Engagement-configurable hero kicker (task 1218070214136694, "remove hardcoded SMBC kicker").
  // Every segment — client name AND the engagement/review label — comes from the selected project's
  // settings (client_name · name), so nothing is burned into the source: not the client, not a
  // "Q3 Review"-style label. When no project is selected the kicker is omitted entirely (an empty
  // kicker beats naming a client or a review that isn't this engagement).
  const engagementKicker = useMemo(() => {
    const client = (currentProject?.client_name || '').trim();
    const label = (currentProject?.name || '').trim();   // the engagement / review label, from settings
    return [client, label].filter(Boolean).join(' · ');
  }, [currentProject?.client_name, currentProject?.name]);

  const onInventoryFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setIngestMsg('Loading…');
    const r = await live.ingest(file, currentProject?.id ? Number(currentProject.id) : null);
    setIngestMsg(r.ok ? `Loaded ${r.count ?? ''} EUCs` : `Failed: ${r.error || 'error'}`);
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of records) c[r.disposition] = (c[r.disposition] || 0) + 1;
    return c;
  }, [records]);

  const committed = records.filter((r) => r.status === 'auto_confirmed' || r.status === 'committed').length;
  const drill = records.filter((r) => r.disposition === selectedBox);
  // QA finding 2026-08-24: this used to fall back to drill[0] — the first row in UNSORTED order,
  // which is usually well outside the Top EUCs list's band+confidence top 10. TopEucList
  // auto-expands to reveal a selection below the cap (Edit 1), so that arbitrary default made the
  // list open FULLY EXPANDED on every load — exactly the screen real estate Edit 2 was meant to
  // reclaim. Defaulting to the top-ranked EUC keeps the cap intact and is the more useful default
  // anyway: the worked example opens on the highest-priority EUC, not an arbitrary one.
  const selected = records.find((r) => r.euc_id === selectedEucId) || topRanked(drill) || topRanked(records);

  // Turn 5 · 5b: the Consolidate / Decommission secondary counts drill into the clusters surface.
  if (showClusters) {
    return (
      <div style={{ fontFamily: font.ui, color: C.body }}>
        <Clusters
          clusters={clusters}
          nearMisses={live.nearMisses}
          onOverrideSurvivor={live.overrideSurvivor}
          onBack={() => setShowClusters(false)}
        />
      </div>
    );
  }

  // Empty state — no inventory ingested yet. No demo data is ever shown, so any number the screen
  // renders is the tenant's real ingest. A clear upload call-to-action stands in until then.
  if (!recordsProp && records.length === 0) {
    return (
      <div style={{ fontFamily: font.ui, color: C.body }}>
        <div style={{ maxWidth: 560, margin: '48px auto', textAlign: 'center', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, padding: '40px 32px' }}>
          <div style={{ fontFamily: font.ui, fontSize: 11, letterSpacing: '0.12em', color: C.label, textTransform: 'uppercase' }}>Disposition Framework</div>
          {live.loading ? (
            // Fetching the snapshot — show only the loading state, never the upload CTA alongside it.
            <h2 style={{ margin: '14px 0 4px', fontSize: 18, fontWeight: 800, color: C.ink }}>Loading…</h2>
          ) : (
            <>
              <h2 style={{ margin: '10px 0 6px', fontSize: 18, fontWeight: 800, color: C.ink, letterSpacing: '-0.02em' }}>No inventory loaded yet</h2>
              <p style={{ fontSize: 13.5, color: C.subtle, margin: '0 0 20px' }}>
                Intake starts on the <b>EUC Inventory</b> tab — load the Apparity inventory workbook (.xlsx), then attach source files to each row. Once the inventory lands, this screen shows the six-outcome plan: where each EUC goes and when. You can also load the workbook here.
              </p>
              <button onClick={() => fileRef.current?.click()} disabled={live.ingesting} style={{
                cursor: live.ingesting ? 'default' : 'pointer', fontSize: 13.5, fontWeight: 700, color: '#fff',
                background: C.accent, border: 'none', borderRadius: 11, padding: '11px 20px',
              }}>
                {live.ingesting ? 'Loading…' : 'Load inventory (.xlsx)'}
              </button>
              <input ref={fileRef} type="file" accept=".xlsx" onChange={onInventoryFile} style={{ display: 'none' }} />
              {ingestMsg && <div style={{ fontSize: 12, color: C.accent, marginTop: 12 }}>{ingestMsg}</div>}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: font.ui, color: C.body }}>
      {/* Hero */}
      <div style={{ marginBottom: 22 }}>
        {/* Engagement-configurable kicker (task 1218070214136694): reads the engagement name from
            the current project, never a hardcoded client. When no project is selected the kicker
            is omitted entirely — the product chrome must not name a tenant. */}
        {engagementKicker && (
          <div style={{ fontFamily: font.ui, fontSize: 11, letterSpacing: '0.12em', color: C.label, textTransform: 'uppercase' }}>
            {engagementKicker}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.ink, letterSpacing: '-0.01em' }}>
            Here's the plan for all {records.length} EUCs.
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', padding: '3px 8px', borderRadius: 999,
              background: live.source === 'live' ? '#dcfce7' : '#f1f0f6', color: live.source === 'live' ? '#166534' : C.label }}>
              {live.source === 'live' ? 'LIVE DATA' : 'PREVIEW'}
            </span>
            {/* Single intake path (task 1218070214136694, subtask 7): intake lives on EUC Inventory
                (step 1 workbook → step 2 attach files). With an inventory already loaded, Framework
                Mapping shows only a QUIET reload — not a second prominent "Load inventory" hero that
                competes with the Inventory tab's intake CTA. */}
            <button onClick={() => fileRef.current?.click()} disabled={live.ingesting} title="Reload the inventory workbook (intake lives on the EUC Inventory tab)" style={{
              cursor: live.ingesting ? 'default' : 'pointer', fontSize: 12, fontWeight: 600, color: C.subtle,
              background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 9, padding: '6px 12px',
            }}>
              {live.ingesting ? 'Loading…' : 'Reload inventory'}
            </button>
            <input ref={fileRef} type="file" accept=".xlsx" onChange={onInventoryFile} style={{ display: 'none' }} />
            <div style={{ fontFamily: font.mono, fontSize: 12, color: C.subtle }}>
              Ruleset v{ruleset.version} · {committed} committed
            </div>
            {onAdjustFramework && (
              // Item 9 (spec §10.1) — the intended UX entry point into Rule Configuration from
              // the screen a consultant is already looking at.
              <button onClick={onAdjustFramework} style={{
                cursor: 'pointer', fontSize: 12, fontWeight: 700, color: C.accent, background: 'transparent',
                border: 'none', padding: 0,
              }}>
                Adjust framework →
              </button>
            )}
          </div>
        </div>
        {ingestMsg && <div style={{ fontSize: 12, color: C.accent, marginTop: 6 }}>{ingestMsg}</div>}
        <div style={{ fontSize: 13.5, color: C.subtle, marginTop: 4 }}>
          Six plain-English questions decide where each EUC lands and when we act — every answer traces back to a gate or factor.
        </div>
      </div>

      {/* Outcome tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
        {ruleset.boxes.map((b) => {
          const active = selectedBox === b.id;
          return (
            <button key={b.id} onClick={() => setSelectedBox(b.id)} style={{
              textAlign: 'left', cursor: 'pointer', position: 'relative', overflow: 'hidden',
              background: C.surface, border: `${active ? 2 : 1}px solid ${active ? C.accent : C.border}`,
              borderRadius: 16, padding: '16px 16px 16px 22px',
              boxShadow: active ? '0 6px 20px rgba(139,92,246,.16)' : 'none',
            }}>
              <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: BOX_STRIP[b.id] }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: C.ink }}>{b.label}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  {active && <span style={{ fontSize: 11, fontWeight: 800, color: C.accent, letterSpacing: '0.08em', background: '#ede9fe', padding: '2px 6px', borderRadius: 6 }}>VIEWING</span>}
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 600, color: RISK_TEXT[b.residual_risk] }}>{b.residual_risk}</span>
                </div>
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.ink, margin: '6px 0 2px' }}>{counts[b.id] || 0}</div>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: C.subtle }}>
                → {targets[b.id] || b.target_platform_catalog[0]}
              </div>
            </button>
          );
        })}
      </div>

      {/* Secondary counts — Consolidate / Decommission NAVIGATE to the dedicated Consolidation tab
          (one job per screen) when an owner wires onNavigateConsolidation; otherwise they fall back
          to the legacy inline clusters drill (storybook/tests). */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 22, flexWrap: 'wrap' }}>
        {(['decommission', 'consolidate', 'queue'] as const).map((k) => {
          const drillable = k === 'decommission' || k === 'consolidate';
          const onDrill = () => {
            if (!drillable) return;
            if (onNavigateConsolidation) onNavigateConsolidation();
            else setShowClusters(true);
          };
          return (
            <button key={k} onClick={onDrill} disabled={!drillable} style={{
              display: 'flex', alignItems: 'center', gap: 8, background: C.soft, border: `1px solid ${C.border}`,
              borderRadius: 999, padding: '6px 14px', fontSize: 12, cursor: drillable ? 'pointer' : 'default',
            }}>
              <b style={{ color: C.ink }}>{counts[k] || 0}</b>
              <span style={{ color: C.subtle, textTransform: 'capitalize' }}>{k}</span>
              {drillable && <span style={{ color: C.accent, fontSize: 11 }}>→</span>}
            </button>
          );
        })}
      </div>

      {/* Edit 7 — Target systems: which platform each outcome routes to is selectable + editable. */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 16, marginBottom: 22 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: C.ink }}>Target systems</div>
          <div style={{ fontSize: 11, color: C.label }}>Not sure they use these? Pick the client's actual platform — or add one.</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
          {ruleset.boxes.map((b) => (
            <TargetRow key={b.id} box={b}
              options={Array.from(new Set([...b.target_platform_catalog, ...(customTargets[b.id] || [])]))}
              value={targets[b.id] || b.target_platform_catalog[0]}
              onSelect={(v) => setTarget(b.id, v)}
              onAddCustom={(v) => addCustomTarget(b.id, v)} />
          ))}
        </div>
      </div>

      {/* Drill-down: dot plot + Top-EUC list */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr', gap: 16, marginBottom: 26 }}>
        <DotPlot records={drill} selectedEucId={selected?.euc_id} onSelect={setSelectedEucId} />
        <TopEucList records={drill} selectedEucId={selected?.euc_id} onSelect={setSelectedEucId} />
      </div>

      {/* Six questions + worked example */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <SixQuestions answers={selected ? answers[selected.euc_id] : undefined} />
        {selected && (
          <div>
            {/* Edit 3 — title over the black box, matching the "WHERE IT LANDS" label style + line. */}
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: C.label, textTransform: 'uppercase', marginBottom: 8 }}>Specific EUC Classification Rationale</div>
            <WorkedExample record={selected} ruleset={ruleset}
              answers={answers[selected.euc_id] || {}}
              onSetAnswer={(n, t) => setAnswer(selected.euc_id, n, t)}
              customTargets={customTargets}
              eucTargetOverride={eucTargets[selected.euc_id]}
              onSetEucTarget={(v) => onSetEucTarget(selected.euc_id, v)}
              clusters={clusters} records={records}
              onOverride={(disposition, reason) => live.overrideDisposition(selected.euc_id, disposition, reason)} />
            <ArtifactsPanel record={selected} projectId={currentProject?.id ? Number(currentProject.id) : null}
              onAttach={live.attachArtifact} onDetach={live.detachArtifact} onRegroup={live.regroupArtifact} />
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit 7 — one row per outcome box: select the target platform from its catalog, or add a custom
// system (e.g. the client runs Tableau, not Hex). Selection bubbles up and persists client-side.
function TargetRow({ box, options, value, onSelect, onAddCustom }: {
  box: RuleSet['boxes'][number];
  options: string[];
  value: string;
  onSelect: (v: string) => void;
  onAddCustom: (v: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const opts = options.includes(value) ? options : [value, ...options];
  const commit = () => { const v = draft.trim(); if (v) onAddCustom(v); setDraft(''); setAdding(false); };
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, padding: '10px 12px', background: C.soft }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.ink, marginBottom: 7 }}>{box.label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color: C.accent, fontSize: 13, flexShrink: 0 }}>→</span>
        <select value={value}
          onChange={(e) => { if (e.target.value === '__add__') setAdding(true); else onSelect(e.target.value); }}
          style={{ flex: 1, minWidth: 0, fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: C.body,
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 8px', cursor: 'pointer' }}>
          {opts.map((p) => <option key={p} value={p}>{p}</option>)}
          <option value="__add__">+ Add system…</option>
        </select>
      </div>
      {adding && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setAdding(false); setDraft(''); } }}
            placeholder="e.g. Tableau"
            style={{ flex: 1, minWidth: 0, fontSize: 12, color: C.body, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 8px' }} />
          <button type="button" onClick={commit}
            style={{ cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#fff', background: C.accent, border: 'none', borderRadius: 8, padding: '6px 12px' }}>Add</button>
        </div>
      )}
    </div>
  );
}

function DotPlot({ records, selectedEucId, onSelect }: { records: DispositionRecord[]; selectedEucId?: string; onSelect: (id: string) => void }) {
  const bands: ('cat1' | 'cat2' | 'cat3')[] = ['cat1', 'cat2', 'cat3'];
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, padding: 16 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: C.ink, marginBottom: 12 }}>Timing (when we act)</div>
      {bands.map((band) => {
        const rows = records.filter((r) => r.timing === band);
        return (
          <div key={band} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: `1px dashed ${C.border}` }}>
            {/* Edit 8 — label top-aligned + no-wrap; dots laid out in a fixed 20px-column grid so
                they stay aligned no matter how many wrap (was: centered flex-wrap that went ragged). */}
            <div style={{ width: 118, flexShrink: 0, fontSize: 12, lineHeight: '20px', whiteSpace: 'nowrap', color: C.subtle, fontWeight: 600 }}>{TIMING_LABEL[band]}</div>
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 20px)', gap: 8 }}>
              {rows.length === 0 && <span style={{ fontSize: 11, lineHeight: '20px', color: C.label }}>—</span>}
              {rows.map((r) => {
                const on = r.euc_id === selectedEucId;
                const provisional = r.status === 'provisional';
                return (
                  <button key={r.euc_id} title={`${r.file} · ${r.dept} · conf ${r.confidence ?? '—'}`} onClick={() => onSelect(r.euc_id)}
                    style={{ width: 16, height: 16, borderRadius: '50%', cursor: 'pointer', padding: 0,
                      background: C.accent, opacity: provisional ? 0.35 : 1,
                      border: on ? `2px solid ${C.ink}` : '2px solid transparent',
                      boxShadow: on ? '0 2px 8px rgba(139,92,246,.4)' : 'none' }} />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TopEucList({ records, selectedEucId, onSelect }: { records: DispositionRecord[]; selectedEucId?: string; onSelect: (id: string) => void }) {
  // Default sort: materiality band, then confidence (Ryan 2026-07-21).
  const sorted = [...records].sort(byBandThenConfidence);
  // Edit 2, corrected 2026-08 (Ryan 7/30): the original Edit 2 build rendered every row behind a
  // scrollbar — functionally close, but the header still claimed "N total - scroll for more"
  // instead of actually capping. "The intent was screen real estate. Render the top 10 by default
  // with a 'Show all N' expander, header reads 'Top 10 of N.'"
  const [expanded, setExpanded] = useState(false);
  const CAP = 10;
  const selectedIdx = selectedEucId ? sorted.findIndex((r) => r.euc_id === selectedEucId) : -1;
  // Edit 1 — clicking a dot in the Timing chart selects an EUC; scroll its row into view here.
  // A selection outside the default top 10 has to auto-expand first, or there is no row to find.
  useEffect(() => {
    if (selectedIdx >= CAP) setExpanded(true);
  }, [selectedIdx]);
  const visible = expanded ? sorted : sorted.slice(0, CAP);
  // block:'nearest' keeps the scroll inside this list box, never yanking the whole page.
  const selectedRowRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    selectedRowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedEucId, expanded]);
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 12 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: C.ink }}>Top EUCs · by band + confidence</div>
        {sorted.length > CAP && (
          <button type="button" onClick={() => setExpanded((v) => !v)} style={{
            fontSize: 11, color: C.accent, fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          }}>
            {expanded ? `Show top ${CAP}` : `Top ${CAP} of ${sorted.length} · Show all ${sorted.length}`}
          </button>
        )}
      </div>
      {/* Edit 2 — capped to CAP rows by default (Show all expands); still scrollable once expanded
          so Edit 1 can scroll to any selected EUC without stretching the page at rest. */}
      <div style={{ maxHeight: 468, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, paddingRight: 4 }}>
        {visible.map((r) => {
          const on = r.euc_id === selectedEucId;
          const amber = r.status === 'human_review' || r.status === 'provisional';
          return (
            <button key={r.euc_id} ref={on ? selectedRowRef : undefined} onClick={() => onSelect(r.euc_id)} style={{
              textAlign: 'left', cursor: 'pointer', display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 10, alignItems: 'center',
              padding: '9px 11px', borderRadius: 12,
              background: on ? '#ede9fe' : C.surface, border: `1px solid ${on ? C.accent : C.border}`,
              boxShadow: on ? '0 4px 14px rgba(139,92,246,.18)' : 'none',
              borderStyle: r.status === 'provisional' ? 'dashed' : 'solid',
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: C.ink, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.file}</div>
                <div style={{ fontSize: 11, color: C.label }}>
                  {r.dept}
                  {(r.artifacts?.length || 0) > 1 && <span style={{ marginLeft: 6, color: C.accent, fontWeight: 700 }}>· {r.artifacts!.length} files</span>}
                  {r.origin === 'discovered_on_upload' && <span style={{ marginLeft: 6, color: C.accent, fontWeight: 700 }}>· discovered</span>}
                </div>
              </div>
              {r.materiality_band
                ? <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 7px', borderRadius: 999, background: BAND_COLOR[r.materiality_band].bg, color: BAND_COLOR[r.materiality_band].fg, whiteSpace: 'nowrap' }}>{bandLabel(r)}</span>
                : <span style={{ fontSize: 11, color: C.label }}>—</span>}
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 7px', borderRadius: 999,
                background: amber ? '#fef3c7' : '#dcfce7', color: amber ? '#92400e' : '#166534' }}>
                {r.confidence == null ? 'QUEUE' : amber ? 'REVIEW' : `${Math.round((r.confidence) * 100)}%`}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SixQuestions({ answers }: { answers?: Record<string, string> }) {
  const where = QUESTIONS.filter((q) => q.axis === 'where');
  const when = QUESTIONS.filter((q) => q.axis === 'when');
  // Edit 6 — the gate/factor a question projects is a clickable chip; tapping one drills into its
  // definition inline, so the auditor never has to guess what G1 / E3 means. Key = `${q.n}:${id}`.
  const [openKey, setOpenKey] = useState<string | null>(null);
  const Card = (q: typeof QUESTIONS[number]) => {
    const refs = q.refs || [];
    const openId = refs.find((id) => openKey === `${q.n}:${id}`);
    const openDef = openId ? PRIMITIVES[openId] : undefined;
    return (
      <div key={q.n} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '12px 14px' }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#f5f3ff', color: C.accent, fontWeight: 800, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{q.n}</div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: C.ink }}>{q.question}</div>
            <div style={{ fontSize: 12, color: C.subtle, marginTop: 3, lineHeight: 1.5 }}>{q.explain}</div>
            {refs.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 7, alignItems: 'center' }}>
                {refs.map((id) => {
                  const def = PRIMITIVES[id];
                  const isGate = def?.kind === 'gate';
                  const on = openKey === `${q.n}:${id}`;
                  return (
                    <button key={id} type="button" onClick={() => setOpenKey(on ? null : `${q.n}:${id}`)}
                      title={def ? `${id} — ${def.name}` : id}
                      style={{
                        cursor: 'pointer', fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 700,
                        padding: '2px 7px', borderRadius: 999, lineHeight: 1.6,
                        background: on ? C.accent : isGate ? '#efeaff' : '#f4f2fb',
                        color: on ? '#fff' : isGate ? C.deep : C.subtle,
                        border: `1px solid ${on ? C.accent : C.border}`,
                      }}>{id}</button>
                  );
                })}
              </div>
            ) : (
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: C.label, marginTop: 5 }}>{q.gates}</div>
            )}
            {openDef && openId && (
              <div style={{ marginTop: 8, background: C.soft, border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontFamily: font.ui, fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '2px 6px', borderRadius: 5, background: openDef.kind === 'gate' ? '#e6dbff' : '#eceaf6', color: openDef.kind === 'gate' ? C.deep : C.subtle }}>{openDef.kind}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.ink }}>{openId} — {openDef.name}</span>
                </div>
                <div style={{ fontSize: 11, color: C.subtle, marginTop: 4, lineHeight: 1.5 }}>{openDef.detail}</div>
              </div>
            )}
            {/* Edit 4 — the selected EUC's captured answer/rationale for this question (read-only here). */}
            {answers && answers[q.n] && <AnswerEditor value={answers[q.n]} />}
          </div>
        </div>
      </div>
    );
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: C.label, textTransform: 'uppercase' }}>Where it lands</div>
        <div style={{ fontSize: 11, color: C.label }}>Tap a gate or factor to see what it checks</div>
      </div>
      {where.map(Card)}
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: C.label, textTransform: 'uppercase', marginTop: 6 }}>When we act</div>
      {when.map(Card)}
    </div>
  );
}

// Edit 4 / 5 — shows the answer/rationale for a question and (when onSave is given) lets you
// capture/edit it. Precedence: a consultant-captured answer ("ANS") wins; otherwise the engine's
// own projected rationale ("WHY", from defaultRationale) is shown by default so the question is
// never just a bare decision chip; only when there is neither does the empty "+ Add" prompt show.
// `dark` styles it for the black worked-example box; light for the six-question cards.
function AnswerEditor({ value, fallback, onSave, dark, indent = 0 }: {
  value?: string; fallback?: string; onSave?: (t: string) => void; dark?: boolean; indent?: number;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);
  const pal = dark
    ? { label: '#8b8bad', text: '#dcd9f0', border: 'rgba(255,255,255,.18)', bg: 'rgba(255,255,255,.06)', accent: '#a99cff' }
    : { label: C.label, text: C.body, border: C.border, bg: C.soft, accent: C.accent };
  // Editing starts from the consultant's own answer if any, else the engine rationale as a draft.
  const openEditor = () => { setDraft(value || fallback || ''); setEditing(true); };
  if (editing && onSave) {
    return (
      <div style={{ marginLeft: indent, marginTop: 5, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <textarea autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
          placeholder="Capture the answer / rationale for this EUC…"
          onKeyDown={(e) => { if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); } }}
          style={{ width: '100%', minHeight: 52, resize: 'vertical', fontFamily: 'inherit', fontSize: 12,
            color: pal.text, background: pal.bg, border: `1px solid ${pal.border}`, borderRadius: 8, padding: '7px 9px', boxSizing: 'border-box' }} />
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" onClick={() => { onSave(draft); setEditing(false); }}
            style={{ cursor: 'pointer', fontSize: 11, fontWeight: 700, color: '#fff', background: C.accent, border: 'none', borderRadius: 7, padding: '5px 12px' }}>Save</button>
          <button type="button" onClick={() => { setDraft(value || ''); setEditing(false); }}
            style={{ cursor: 'pointer', fontSize: 11, fontWeight: 600, color: pal.label, background: 'transparent', border: `1px solid ${pal.border}`, borderRadius: 7, padding: '5px 12px' }}>Cancel</button>
        </div>
      </div>
    );
  }
  // A consultant-captured answer takes precedence over the engine default.
  if (value) {
    return (
      <div style={{ marginLeft: indent, marginTop: 5, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', color: pal.accent, textTransform: 'uppercase', flexShrink: 0, marginTop: 2 }}>Ans</span>
        <span style={{ fontSize: 12, color: pal.text, lineHeight: 1.5, flex: 1 }}>{value}</span>
        {onSave && <button type="button" onClick={openEditor} style={{ cursor: 'pointer', fontSize: 11, color: pal.accent, background: 'transparent', border: 'none', padding: 0, flexShrink: 0 }}>Edit</button>}
      </div>
    );
  }
  // Edit 4 — the engine's projected rationale, shown by default. Labelled "WHY" (muted) so it reads
  // as engine-derived, distinct from a human "ANS"; the consultant can still capture/override it.
  if (fallback) {
    return (
      <div style={{ marginLeft: indent, marginTop: 5, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', color: pal.label, textTransform: 'uppercase', flexShrink: 0, marginTop: 2 }}>Why</span>
        <span style={{ fontSize: 12, color: pal.text, lineHeight: 1.5, flex: 1, opacity: 0.92 }}>{fallback}</span>
        {onSave && <button type="button" onClick={openEditor} style={{ cursor: 'pointer', fontSize: 11, color: pal.accent, background: 'transparent', border: 'none', padding: 0, flexShrink: 0 }}>+ Add</button>}
      </div>
    );
  }
  if (!onSave) return null;
  return (
    <button type="button" onClick={openEditor}
      style={{ marginLeft: indent, marginTop: 5, cursor: 'pointer', fontSize: 11, fontWeight: 600, color: pal.accent, background: 'transparent', border: `1px dashed ${pal.border}`, borderRadius: 7, padding: '4px 9px', textAlign: 'left' }}>
      + Add answer / rationale
    </button>
  );
}

// Chip vocabulary (handoff §6), derived from the record — no per-EUC math, just projection.
function chipFor(n: number, r: DispositionRecord): { label: string; tone: 'ok' | 'block' | 'review' | 'bias' | 'pick' | 'none' } {
  const fired = (id: string) => r.gates_fired.some((g) => g.id === id);
  switch (n) {
    case 1: return fired('G1') ? { label: 'NO OWNER', tone: 'block' } : { label: 'PASS', tone: 'ok' };
    case 2: return fired('G4') || fired('G5') ? { label: 'MOVE OUT', tone: 'block' } : { label: 'CLEAR', tone: 'ok' };
    case 3: return fired('G6') ? { label: 'TO ORACLE AP', tone: 'bias' } : { label: 'NO ERP HOME', tone: 'none' };
    case 4: return { label: { box1: 'FULL REBUILD', box2: 'GOVERN LIGHT', box3: 'PLATFORM MIG.', box4: 'RETAIN' }[r.disposition as BoxId] || '—', tone: 'pick' };
    case 5: return r.timing === 'cat1' ? { label: 'IMMEDIATE', tone: 'pick' } : { label: 'NOT FORCED', tone: 'none' };
    case 6: return r.timing === 'cat2' ? { label: 'NEAR-TERM', tone: 'pick' } : r.timing === 'cat3' ? { label: 'DEFERRED', tone: 'none' } : { label: 'CLEAR', tone: 'ok' };
    default: return { label: '—', tone: 'none' };
  }
}

// Edit 4 — the engine's own plain-language rationale for each of the six questions, shown under the
// question when the consultant hasn't captured their own answer yet (a captured answer still wins,
// see AnswerEditor). Everything here is PROJECTED from the record — the hard gates that actually
// fired (with the ruleset's own definition of each), the provisional gate's own "needs" hint, and
// reasoning_summary — never an invented fact. So a "PASS" chip becomes a real sentence explaining
// why it passed, not just the three-letter chip Ryan flagged.
const BOX_RATIONALE: Record<string, string> = {
  box1: 'Complex and stable — routed to a full SDLC rebuild on the target platform.',
  box2: 'Material but change-heavy — governed light SDLC keeps agility with controls.',
  box3: 'A system-of-record should own this value — migrated upstream to the platform.',
  box4: 'Simple, stable and low-risk — retained under IT change control.',
  consolidate: 'Overlaps a canonical survivor — merged into it, then retired.',
  decommission: 'Dead and superseded — retired.',
  queue: 'Parked in the resolution queue until its blocker clears.',
};

function defaultRationale(n: number, r: DispositionRecord): string {
  const fired = (id: string) => r.gates_fired.some((g) => g.id === id);
  const gate = (id: string) => PRIMITIVES[id]?.detail || '';
  const evalOf = (id: string) => (r.gate_evaluations || []).find((g) => g.id === id);
  // A provisional gate can name what it's still waiting on — surface it verbatim, nothing invented.
  const needs = (...ids: string[]) => {
    for (const id of ids) {
      const e = evalOf(id);
      if (e?.needs_hint) return ` Still needs: ${e.needs_hint}${e.needs_owner ? ` (${e.needs_owner})` : ''}.`;
    }
    return '';
  };
  switch (n) {
    case 1:
      if (fired('G1')) return gate('G1') + needs('G1');
      if (fired('G2')) return gate('G2');
      return 'Has an accountable owner and recent usage — neither the no-owner nor the dead-and-superseded gate fires.';
    case 2:
      if (fired('G4')) return gate('G4');
      if (fired('G5')) return gate('G5');
      return 'Not SOX-material and holds no regulated data — no gate forbids Box 4 on these grounds.';
    case 3:
      if (fired('G6')) return gate('G6') + needs('G6');
      return 'No system-of-record already owns this value — it is not pushed upstream to an ERP.';
    case 4:
      return r.reasoning_summary || r.provisional_reason || BOX_RATIONALE[r.disposition] || '';
    case 5:
      if (fired('G7')) return gate('G7') + needs('G7');
      return 'No regulatory forcing event on the calendar — timing is not forced to immediate.';
    case 6:
      if (r.timing === 'cat2') return 'A straight lift would bake in manual steps or dirty lineage — moved to near-term optimization.';
      if (r.timing === 'cat3') return 'Deferred — an unresolved dependency (or a missing extraction run) outranks near-term action.';
      return 'Lifting it as-is would not lock in material tech debt.';
    default:
      return '';
  }
}
const TONE: Record<string, { bg: string; fg: string }> = {
  ok: { bg: '#dcfce7', fg: '#166534' }, block: { bg: '#fee2e2', fg: '#991b1b' }, review: { bg: '#fef3c7', fg: '#92400e' },
  bias: { bg: '#dbeafe', fg: '#1e40af' }, pick: { bg: '#ede9fe', fg: '#5b21b6' }, none: { bg: '#f1f0f6', fg: '#6b6a83' },
};

// Amber is reserved for provisional / contested / needs-decision states (turn5 §0) — the one
// exception to the purple-only rule, and it applies only in 5a/5b.
const AMBER = '#f59e0b';
const OUTCOME_LABEL: Record<string, string> = { box1: 'Full SDLC Rebuild', box2: 'Governed / Light SDLC', box3: 'Platform Migration', box4: 'Retain & Improve', decommission: 'Decommission', consolidate: 'Consolidate', queue: 'Resolution Queue' };

// Turn 5 · 5a — the provisional/deferred step-row chip, derived from the gate evaluation for
// each of the six questions (§1.3). Amber = blocking/unresolved; neutral = contingent/deferred.
const Q_GATE: Record<number, string> = { 1: 'G1', 2: 'G4', 3: 'G6', 5: 'G7' };
function provStep(n: number, r: DispositionRecord): { label: string; dot: string; bg: string; fg: string; sub?: string } {
  const ev = (r.gate_evaluations || []).find((g) => g.id === Q_GATE[n]);
  const st = ev?.status;
  const neutral = { dot: '#8b8bad', bg: '#26243f', fg: '#b9b6d8' };
  const amber = { dot: AMBER, bg: '#3a2c12', fg: '#f2c17a' };
  const green = { dot: '#10b981', bg: '#123227', fg: '#71d6ab' };
  const hint: string | undefined = ev?.needs_hint || undefined;
  if (st === 'blocking') return { label: 'BLOCKING', ...amber, sub: hint ? `Needs: ${ev?.needs_owner || 'owner'} to ${hint}${ev?.needs_eta ? ` · ETA ${ev.needs_eta}` : ''}` : undefined };
  if (st === 'unresolved') return { label: 'UNRESOLVED', ...amber, sub: hint ? `Needs: ${hint}` : undefined };
  if (st === 'contingent') return { label: 'CONTINGENT', ...neutral, sub: ev?.contingent_on?.length ? `If ${ev.contingent_on.join(', ')} clears: ${hint || 'this resolves'}` : hint };
  if (st === 'deferred') return { label: 'DEFERRED', ...neutral, sub: 'Not evaluated until a prior blocker resolves' };
  if (st === 'pass' || st === 'clear') return { label: st.toUpperCase(), ...green };
  return { label: 'NOT EVALUATED', ...neutral };
}

// Turn 5 · 5a — variant that renders when a record is provisional or deferred (§1).
//
// Item 18 (Asana 1216373781992284): "Cat 3 provisional leads with the resolution block (what
// unknown must clear) instead of the six-question trail." Before this item the resolution panel
// sat BELOW the full six-question trail, with equal visual weight — the same layout a normal
// auto-confirmed record gets. That's backwards for a record that, BY DEFINITION, can't answer
// most of those six questions yet (that's why it's provisional): the one thing a consultant
// actually needs first is "what has to clear, and what happens either way," not six rows of
// mostly BLOCKING/UNRESOLVED/DEFERRED chips. The resolution block now renders first; the
// six-question trail is still fully present (nothing removed — the AnswerEditor per-question
// override is still real functionality) but collapsed behind a closed-by-default toggle.
function ProvisionalWorkedExample({ record: r, answers, onSetAnswer }: { record: DispositionRecord; answers: Record<string, string>; onSetAnswer: (n: number, t: string) => void }) {
  const deferred = r.status === 'deferred';
  const blockers = r.blockers || (r.gate_evaluations || []).filter((g) => g.status === 'blocking' || g.status === 'unresolved').map((g) => g.id);
  const branches = r.contingent_branches || [];
  const [showTrail, setShowTrail] = useState(false);
  return (
    <div style={{ background: color.navy, borderRadius: 20, padding: 18, color: '#e7e6f4', border: `1px dashed ${AMBER}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        {r.source_type && <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 6, background: '#2a2650', color: '#c9c6ee' }}>{r.source_type.toUpperCase()}</span>}
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12.5, fontWeight: 600 }}>{r.file}</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 800, color: AMBER, letterSpacing: '0.08em', display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: AMBER }} />{deferred ? 'DEFERRED · UNANALYZED' : 'PROVISIONAL'}
        </span>
      </div>
      <div style={{ fontSize: 11, color: '#9a97c4', marginBottom: 12 }}>{r.dept} · {bandLabel(r)}</div>

      {/* Resolution block LEADS (item 18) — what's blocking + the two possible outcomes, the
          thing a consultant actually needs first for a record that can't be dispositioned yet. */}
      <div style={{ background: '#1a0f3d', border: `1px solid rgba(245,158,11,.35)`, borderRadius: 14, padding: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: AMBER, letterSpacing: '0.08em' }}>STATUS · {deferred ? 'DEFERRED · NOT YET ANALYZED' : 'PROVISIONAL · CANNOT DISPOSITION YET'}</div>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: '#fff', margin: '6px 0 12px' }}>
          {deferred ? 'Not yet analyzed — run extraction to route it.' : `Parked in the Cat 3 queue. ${blockers.length} blocker${blockers.length === 1 ? '' : 's'} must clear before the engine can pick a box.`}
        </div>

        {!deferred && blockers.length > 0 && (
          <div style={{ background: 'rgba(255,255,255,.06)', borderRadius: 10, padding: '10px 12px', marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#c9c6ee', letterSpacing: '0.06em', marginBottom: 6 }}>WHAT HAS TO CLEAR</div>
            {(r.gate_evaluations || []).filter((g) => g.status === 'blocking' || g.status === 'unresolved').map((g, i) => (
              <div key={g.id} style={{ display: 'flex', gap: 8, fontSize: 12, marginBottom: 3 }}>
                <span style={{ color: AMBER, fontWeight: 800 }}>{i + 1}.</span>
                <span style={{ color: '#dcd9f0' }}><b>{g.needs_hint || g.id}</b>{g.needs_owner ? ` · ${g.needs_owner}` : ''}{g.needs_eta ? ` · ETA ${g.needs_eta}` : ''}</span>
              </div>
            ))}
          </div>
        )}

        {branches.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#c9c6ee', letterSpacing: '0.06em', marginBottom: 6 }}>TWO POSSIBLE OUTCOMES</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {branches.slice(0, 2).map((b, i) => (
                <div key={i} style={{ background: 'rgba(139,92,246,.14)', border: '1px solid rgba(139,92,246,.35)', borderRadius: 10, padding: '9px 10px' }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: AMBER, letterSpacing: '0.05em', marginBottom: 3 }}>{(b.condition || '').toUpperCase()}</div>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: '#fff' }}>{OUTCOME_LABEL[b.disposition] || b.disposition}</div>
                  <div style={{ fontSize: 11, color: '#b9b6d8', marginTop: 3 }}>{b.justification}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Six-question trail — still fully present (per-question AnswerEditor override is real,
          not decorative), just collapsed behind a toggle instead of leading the panel. */}
      <button type="button" onClick={() => setShowTrail((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', background: 'transparent', border: 'none', color: '#9a97c4', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', cursor: 'pointer', padding: '10px 2px 2px' }}>
        <span style={{ transform: showTrail ? 'rotate(90deg)' : 'none', transition: 'transform 120ms ease', display: 'inline-block' }}>›</span>
        {showTrail ? 'HIDE' : 'SHOW'} SIX-QUESTION TRAIL
      </button>
      {showTrail && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 8 }}>
          {QUESTIONS.map((q) => {
            const s = provStep(q.n, r);
            return (
              <div key={q.n} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 12 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.dot, marginTop: 5, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ color: '#cbc9e6' }}>{q.question}</span>
                  {s.sub && <div style={{ fontSize: 11, color: '#8b8bad', marginTop: 2 }}>{s.sub}</div>}
                  {/* Edit 4 / 5 — engine rationale by default (fallback), consultant answer overrides. */}
                  <AnswerEditor value={answers[q.n]} fallback={defaultRationale(q.n, r)} onSave={(t) => onSetAnswer(q.n, t)} dark />
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: s.bg, color: s.fg, whiteSpace: 'nowrap' }}>{s.label}</span>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ fontFamily: font.ui, fontSize: 11, color: '#8b8bad', marginTop: 12 }}>
        In the framework this is Cat 3 ({deferred ? 'Deferred · unanalyzed' : 'Deferred'}) · {r.status}. {blockers.length} gate{blockers.length === 1 ? '' : 's'} unresolved{blockers.length ? ` (${blockers.join(', ')})` : ''}. Auto-promotes to a committed box when all clear. Pinned to ruleset v{r.ruleset_version}.
      </div>
    </div>
  );
}

// Item 18 (Asana 1216373781992284): "Consolidate survivor shows its retiree artifacts." A
// survivor's OWN disposition is its real box (e.g. box3) — only the retirees get
// disposition:'consolidate' (see fixtures.ts's EUC-211). So without this panel, opening a
// survivor's worked example looked identical to any other box3 record, with zero indication it's
// absorbing other EUCs. Resolves the cluster via r.cluster_id, then resolves each OTHER member id
// to its own DispositionRecord (file/dept) via the full `records` list — falling back to just the
// id if that member isn't in the currently-loaded record set (e.g. filtered out by the active box
// tab) rather than silently dropping it from the list.
function RetireeArtifactsPanel({ record: r, clusters, records }: { record: DispositionRecord; clusters: ClusterRecord[]; records: DispositionRecord[] }) {
  const cluster = clusters.find((c) => c.id === r.cluster_id);
  if (!cluster) return null;
  const recordById = new Map(records.map((x) => [x.euc_id, x]));
  const retireeIds = cluster.member_ids.filter((id) => id !== r.euc_id);
  if (!retireeIds.length) return null;

  return (
    <div style={{ background: 'rgba(139,92,246,.10)', border: '1px solid rgba(139,92,246,.3)', borderRadius: 14, padding: 14, marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: '#c9c6ee', letterSpacing: '0.08em' }}>
          CONSOLIDATION SURVIVOR · {retireeIds.length} RETIREE{retireeIds.length === 1 ? '' : 'S'} MERGE INTO THIS EUC
        </div>
        {cluster.estimated_monthly_hours_saved != null && (
          <div style={{ fontSize: 11, color: '#b9b6d8' }}>
            {cluster.estimated_monthly_hours_basis === 'placeholder' ? '~' : ''}{cluster.estimated_monthly_hours_saved} hrs/mo est. saved
          </div>
        )}
      </div>
      <div style={{ fontSize: 11, color: '#dcd9f0', marginBottom: 10, lineHeight: 1.5 }}>{cluster.survivor_selection_rationale}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {retireeIds.map((id) => {
          const retiree = recordById.get(id);
          return (
            <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,.06)', borderRadius: 10, padding: '8px 10px' }}>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 700, color: '#fff' }}>{id}</span>
              <span style={{ flex: 1, fontSize: 11, color: '#cbc9e6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {retiree ? `${retiree.file} · ${retiree.dept}` : 'Not in the current record set'}
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: 'rgba(245,158,11,.18)', color: AMBER, whiteSpace: 'nowrap' }}>
                {retiree?.retirement_reason?.startsWith('consolidated_into') ? 'RETIRING' : (retiree?.status || 'RETIRING').toUpperCase()}
              </span>
            </div>
          );
        })}
      </div>
      {cluster.survivor_override && (
        <div style={{ fontSize: 11, color: '#b9b6d8', marginTop: 8 }}>
          Survivor overridden by a consultant: {cluster.survivor_override.reason}
        </div>
      )}
    </div>
  );
}

function WorkedExample({ record: r, ruleset, answers, onSetAnswer, customTargets, eucTargetOverride, onSetEucTarget, onOverride, clusters = [], records = [] }: {
  record: DispositionRecord; ruleset: RuleSet; answers: Record<string, string>; onSetAnswer: (n: number, t: string) => void;
  // Item 7 (Asana 1216373781992284): the per-engagement catalog additions (Edit 7's "+ Add
  // system…") plus this EUC's own optimistic pick (shown immediately, before the reload that
  // follows a save lands) and the setter that persists + reloads.
  customTargets: Record<string, string[]>;
  eucTargetOverride?: string;
  onSetEucTarget: (value: string) => void;
  onOverride?: (disposition: string, reason: string) => Promise<{ ok: boolean; error?: string }>;
  clusters?: ClusterRecord[];
  records?: DispositionRecord[];
}) {
  // Turn 5 · 5a: provisional/deferred records (or an unresolved gate) render the amber variant.
  const isProvisional = r.status === 'provisional' || r.status === 'deferred' || r.disposition == null
    || (r.gate_evaluations || []).some((g) => g.status === 'blocking' || g.status === 'unresolved');
  if (isProvisional) return <ProvisionalWorkedExample record={r} answers={answers} onSetAnswer={onSetAnswer} />;

  const box = ruleset.boxes.find((b) => b.id === r.disposition);
  return (
    <div style={{ background: C.ink, borderRadius: 20, padding: 18, color: '#e7e6f4' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        {r.source_type && <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 6, background: '#2a2650', color: '#c9c6ee' }}>{r.source_type.toUpperCase()}</span>}
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12.5, fontWeight: 600 }}>{r.file}</span>
        {r.is_survivor && (
          <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 800, padding: '2px 7px', borderRadius: 999, background: 'rgba(139,92,246,.18)', color: '#c9c6ee', letterSpacing: '0.05em' }}>
            CONSOLIDATION SURVIVOR
          </span>
        )}
      </div>
      <div style={{ fontSize: 11, color: '#9a97c4', marginBottom: 12 }}>{r.dept} · {bandLabel(r)}</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {QUESTIONS.map((q) => {
          const c = chipFor(q.n, r);
          return (
            <div key={q.n} style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
                <span style={{ width: 16, color: '#7d7ab0' }}>{q.n}</span>
                <span style={{ flex: 1, color: '#cbc9e6' }}>{q.question}</span>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: TONE[c.tone].bg, color: TONE[c.tone].fg }}>{c.label}</span>
              </div>
              {/* Edit 4 / 5 — engine rationale by default (fallback), consultant answer overrides. */}
              <AnswerEditor value={answers[q.n]} fallback={defaultRationale(q.n, r)} onSave={(t) => onSetAnswer(q.n, t)} dark indent={26} />
            </div>
          );
        })}
      </div>

      <div style={{ background: 'linear-gradient(135deg,#221551,#5b3fc4)', borderRadius: 14, padding: 14, marginTop: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#ded9ff', letterSpacing: '0.08em' }}>{r.confidence == null ? 'ROUTED' : `CONFIDENCE ${Math.round((r.confidence || 0) * 100)}%`}</div>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', margin: '4px 0 8px' }}>{r.reasoning_summary}</div>
        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#e7e3ff' }}>
          {box ? `${box.label} × ${TIMING_LABEL[r.timing]}` : r.disposition} · {r.downstream_path || 'PathB'} · pinned to ruleset v{r.ruleset_version}
        </div>
        {/* Item 7 — per-EUC target platform. selected_target_platform is server-resolved (an
            explicit pick for THIS EUC, else the box's tenant default, else the ruleset seed —
            never a leftover "test"); eucTargetOverride shows the just-saved value immediately,
            before the reload that follows a change lands. */}
        {box && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,.2)' }}>
            <span style={{ fontFamily: font.ui, fontSize: 11, color: '#e7e3ff', flexShrink: 0 }}>TARGET PLATFORM</span>
            <select
              value={eucTargetOverride || r.selected_target_platform || box.target_platform_catalog[0]}
              onChange={(e) => onSetEucTarget(e.target.value)}
              style={{
                flex: 1, minWidth: 0, fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 600,
                color: '#fff', background: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.3)',
                borderRadius: 7, padding: '4px 7px', cursor: 'pointer',
              }}
            >
              {Array.from(new Set([...box.target_platform_catalog, ...(customTargets[box.id] || [])])).map((p) => (
                <option key={p} value={p} style={{ color: '#0f1226' }}>{p}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {r.is_survivor && r.cluster_id && <RetireeArtifactsPanel record={r} clusters={clusters} records={records} />}
      {onOverride && <DispositionOverride record={r} ruleset={ruleset} onOverride={onOverride} />}
    </div>
  );
}

// Item 10 (spec §7.3) — a consultant overriding one EUC's box. The reason is required (>=10 chars,
// enforced server-side too) — same bar as the survivor override on the Consolidation screen. The
// engine's own verdict (`engine_disposition`) stays visible whenever an override is active, so
// this is never a silent edit.
function DispositionOverride({ record: r, ruleset, onOverride }: { record: DispositionRecord; ruleset: RuleSet; onOverride: (disposition: string, reason: string) => Promise<{ ok: boolean; error?: string }> }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(r.disposition);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const options = [...ruleset.boxes.map((b) => ({ id: b.id, label: b.label })), { id: 'decommission', label: 'Decommission' }, { id: 'consolidate', label: 'Consolidate' }];

  const submit = async () => {
    setSaving(true);
    const res = await onOverride(target, reason);
    setSaving(false);
    if (res.ok) { setOpen(false); setReason(''); } else { window.alert(res.error || 'Override failed'); }
  };

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,.1)' }}>
      {r.disposition_override ? (
        <div style={{ fontSize: 11, color: '#ded9ff' }}>
          <b style={{ color: '#fff' }}>Overridden</b> from the engine's {r.engine_disposition} by {r.disposition_override.actor.name || r.disposition_override.actor.email || `user #${r.disposition_override.actor.id}`}
          {' '}— "{r.disposition_override.reason}"
        </div>
      ) : !open ? (
        <button onClick={() => setOpen(true)} style={{ fontSize: 11, fontWeight: 700, color: '#fcd34d', background: 'transparent', border: '1px dashed #f59e0b', borderRadius: 999, padding: '4px 10px', cursor: 'pointer' }}>
          Override this box
        </button>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          <select value={target} onChange={(e) => setTarget(e.target.value as any)}
            style={{ fontFamily: font.ui, fontSize: 11, color: '#e7e6f4', background: '#2a2650', border: '1px solid #453f7a', borderRadius: 8, padding: '6px 8px' }}>
            {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Why (min. 10 characters — required, this is an audit trail)"
            style={{ fontSize: 11, color: '#e7e6f4', background: '#2a2650', border: '1px solid #453f7a', borderRadius: 8, padding: '6px 8px', resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={submit} disabled={reason.trim().length < 10 || saving}
              style={{ cursor: reason.trim().length < 10 || saving ? 'default' : 'pointer', opacity: reason.trim().length < 10 || saving ? 0.5 : 1, fontSize: 11, fontWeight: 700, color: '#fff', background: '#f59e0b', border: 'none', borderRadius: 8, padding: '6px 12px' }}>
              {saving ? 'Saving…' : 'Confirm override'}
            </button>
            <button onClick={() => { setOpen(false); setReason(''); }} style={{ fontSize: 11, color: '#c9c2f5', background: 'transparent', border: 'none', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Item 3 ("EUC-to-artifact is one-to-many, and uploads can create inventory rows"). Shows what's
// actually attached to this EUC and lets a consultant detach a run or move it to a different EUC
// — a deliberate action, never a fuzzy auto-match (eucGrouping.js does no content-based matching
// on purpose). A discovered-on-upload EUC only exists because of its attached run(s), so it gets
// an explicit "DISCOVERED ON UPLOAD" tag distinguishing it from the 154-row Apparity baseline.
function ArtifactsPanel({ record: r, projectId, onAttach, onDetach, onRegroup }: {
  record: DispositionRecord;
  projectId?: number | null;
  onAttach: (eucId: string, runId: string, projectId?: number | null) => Promise<{ ok: boolean; error?: string }>;
  onDetach: (eucId: string, runId: string, projectId?: number | null) => Promise<{ ok: boolean; error?: string }>;
  onRegroup: (fromEucId: string, toEucId: string, runId: string, projectId?: number | null) => Promise<{ ok: boolean; error?: string }>;
}) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [attachRunId, setAttachRunId] = useState('');
  const [regroupTarget, setRegroupTarget] = useState<Record<string, string>>({});
  const artifacts = r.artifacts || [];

  const run = async (fn: () => Promise<{ ok: boolean; error?: string }>, doneMsg: string) => {
    setBusy(true); setMsg('');
    const res = await fn();
    setMsg(res.ok ? doneMsg : `Failed: ${res.error || 'error'}`);
    setBusy(false);
  };

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 16, marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: C.ink }}>
          Attached artifacts ({artifacts.length})
          {r.origin === 'discovered_on_upload' && (
            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 800, padding: '2px 7px', borderRadius: 999, background: '#e6eafc', color: '#3a3a8a', letterSpacing: '0.04em' }}>DISCOVERED ON UPLOAD</span>
          )}
        </div>
        <div style={{ fontSize: 11, color: C.label }}>Scored over the union of everything attached here.</div>
      </div>

      {artifacts.length === 0 && (
        <div style={{ fontSize: 12, color: C.label, marginBottom: 10 }}>No decomposed files attached — this row's disposition is the Apparity label only.</div>
      )}
      {artifacts.map((a) => (
        <div key={a.run_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: `1px solid ${C.border}` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.file || a.run_id}</div>
            <div style={{ fontSize: 11, color: C.label, fontFamily: 'JetBrains Mono, monospace' }}>{a.run_id.slice(0, 8)} · added {new Date(a.added_at).toLocaleDateString()}</div>
          </div>
          <input
            placeholder="Move to EUC id…"
            value={regroupTarget[a.run_id] || ''}
            onChange={(e) => setRegroupTarget((s) => ({ ...s, [a.run_id]: e.target.value }))}
            style={{ width: 130, fontSize: 11, padding: '5px 8px', borderRadius: 8, border: `1px solid ${C.border}` }}
          />
          <button disabled={busy || !(regroupTarget[a.run_id] || '').trim()}
            onClick={() => run(() => onRegroup(r.euc_id, (regroupTarget[a.run_id] || '').trim(), a.run_id, projectId), 'Moved.')}
            style={{ fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.soft, color: C.ink, cursor: busy ? 'default' : 'pointer' }}>
            Move
          </button>
          <button disabled={busy}
            onClick={() => run(() => onDetach(r.euc_id, a.run_id, projectId), 'Detached.')}
            style={{ fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 8, border: '1px solid #f0c9c9', background: '#fdeeee', color: '#9b1c1c', cursor: busy ? 'default' : 'pointer' }}>
            Detach
          </button>
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
        <input
          placeholder="Attach a decomposed run id…"
          value={attachRunId}
          onChange={(e) => setAttachRunId(e.target.value)}
          style={{ flex: 1, fontSize: 11, padding: '6px 10px', borderRadius: 8, border: `1px solid ${C.border}` }}
        />
        <button disabled={busy || !attachRunId.trim()}
          onClick={() => run(() => onAttach(r.euc_id, attachRunId.trim(), projectId), 'Attached.').then(() => setAttachRunId(''))}
          style={{ fontSize: 11, fontWeight: 700, padding: '6px 12px', borderRadius: 8, border: 'none', background: C.accent, color: '#fff', cursor: busy ? 'default' : 'pointer' }}>
          Attach
        </button>
      </div>
      {msg && <div style={{ fontSize: 11, color: msg.startsWith('Failed') ? '#9b1c1c' : C.label, marginTop: 8 }}>{msg}</div>}
    </div>
  );
}
